import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiException } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { Modal } from '../../components/ui/modal';
import { fmtBRL, fmtCompetencia, fmtDate } from '../../lib/format';
import { TrendingUp, TrendingDown, Wallet, Plus, Trash2 } from 'lucide-react';

interface LinhaFin { competencia: string; receita: number; despesa: number; devolucao: number; lucroLiquido: number; }
interface ResumoFin { linhas: LinhaFin[]; totais: { receita: number; despesa: number; devolucao: number; lucroLiquido: number }; }
interface Categoria { id: number; codigo: string; nome: string; natureza: string; }
interface Despesa { id: string; categoria: string | null; descricao: string; valor: number; dataDespesa: string; status: string; }

export function FinanceiroResumoPage() {
  const qc = useQueryClient();
  const [novaDespesa, setNovaDespesa] = useState(false);
  const [excluindo, setExcluindo] = useState<Despesa | null>(null);
  const [form, setForm] = useState({ categoriaId: '', descricao: '', valor: '', dataDespesa: new Date().toISOString().slice(0, 10) });
  const [erro, setErro] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['financeiro-resumo'], queryFn: () => api<ResumoFin>('/financeiro/resumo') });
  const { data: cats } = useQuery({ queryKey: ['financeiro-categorias'], queryFn: () => api<{ items: Categoria[] }>('/financeiro/categorias') });
  const { data: despesas } = useQuery({ queryKey: ['financeiro-despesas'], queryFn: () => api<{ items: Despesa[] }>('/financeiro/despesas') });

  const invalidarTudo = () => {
    qc.invalidateQueries({ queryKey: ['financeiro-resumo'] });
    qc.invalidateQueries({ queryKey: ['financeiro-despesas'] });
  };

  const criar = useMutation({
    mutationFn: () => api('/financeiro/despesas', {
      method: 'POST',
      body: { categoriaId: Number(form.categoriaId), descricao: form.descricao, valor: Number(form.valor), dataDespesa: form.dataDespesa, statusFinanceiro: 'pago' },
    }),
    onSuccess: () => { invalidarTudo(); setNovaDespesa(false); setForm({ categoriaId: '', descricao: '', valor: '', dataDespesa: new Date().toISOString().slice(0, 10) }); },
    onError: (e) => setErro(e instanceof ApiException ? e.message : 'Erro ao lançar despesa'),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => api(`/financeiro/despesas/${id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidarTudo(); setExcluindo(null); },
    onError: (e) => setErro(e instanceof ApiException ? e.message : 'Erro ao excluir'),
  });

  if (isLoading) return <div className="flex justify-center p-12"><Spinner /></div>;

  const t = data?.totais ?? { receita: 0, despesa: 0, devolucao: 0, lucroLiquido: 0 };
  const linhas = data?.linhas ?? [];
  const maxValor = Math.max(...linhas.map((l) => l.receita), 1);

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Financeiro</h1>
          <p className="text-sm text-muted-foreground">Receita, despesa e lucro líquido por competência</p>
        </div>
        <Button onClick={() => { setNovaDespesa(true); setErro(null); }}><Plus className="h-4 w-4 mr-1" /> Lançar despesa</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-green-600" /> Receita</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-green-700">{fmtBRL(t.receita)}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500 flex items-center gap-2"><TrendingDown className="h-4 w-4 text-red-600" /> Despesa</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-red-700">{fmtBRL(t.despesa)}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500 flex items-center gap-2"><TrendingDown className="h-4 w-4 text-amber-600" /> Devoluções</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-amber-700">{fmtBRL(t.devolucao)}</p></CardContent>
        </Card>
        <Card className="border-2 border-blue-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500 flex items-center gap-2"><Wallet className="h-4 w-4 text-blue-600" /> Lucro Líquido</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-blue-700">{fmtBRL(t.lucroLiquido)}</p></CardContent>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader><CardTitle>Por competência</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-2">Competência</th>
                <th className="py-2 text-right">Receita</th>
                <th className="py-2 text-right">Despesa</th>
                <th className="py-2 text-right">Devolução</th>
                <th className="py-2 text-right">Lucro Líquido</th>
                <th className="py-2 w-1/4">Volume</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.competencia} className="border-b">
                  <td className="py-2 font-medium">{fmtCompetencia(l.competencia)}</td>
                  <td className="py-2 text-right text-green-700">{fmtBRL(l.receita)}</td>
                  <td className="py-2 text-right text-red-700">{l.despesa ? fmtBRL(l.despesa) : '—'}</td>
                  <td className="py-2 text-right text-amber-700">{l.devolucao ? fmtBRL(l.devolucao) : '—'}</td>
                  <td className={`py-2 text-right font-semibold ${l.lucroLiquido < 0 ? 'text-red-700' : 'text-blue-700'}`}>{fmtBRL(l.lucroLiquido)}</td>
                  <td className="py-2"><div className="h-3 rounded bg-green-100"><div className="h-3 rounded bg-green-500" style={{ width: `${(l.receita / maxValor) * 100}%` }} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Despesas lançadas</CardTitle></CardHeader>
        <CardContent>
          {(despesas?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">Nenhuma despesa lançada.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="py-2">Data</th>
                  <th className="py-2">Categoria</th>
                  <th className="py-2">Descrição</th>
                  <th className="py-2 text-right">Valor</th>
                  <th className="py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {despesas?.items.map((d) => (
                  <tr key={d.id} className="border-b">
                    <td className="py-2">{fmtDate(d.dataDespesa)}</td>
                    <td className="py-2">{d.categoria ?? '—'}</td>
                    <td className="py-2">{d.descricao}</td>
                    <td className="py-2 text-right text-red-700">{fmtBRL(d.valor)}</td>
                    <td className="py-2 text-right">
                      <button className="text-gray-400 hover:text-red-600" onClick={() => { setExcluindo(d); setErro(null); }} title="Excluir">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {novaDespesa && (
        <Modal open onClose={() => setNovaDespesa(false)} title="Lançar despesa">
          <div className="space-y-3">
            {erro && <div className="rounded bg-red-50 border border-red-200 p-2 text-sm text-red-800">{erro}</div>}
            <select className="w-full rounded border p-2 text-sm" value={form.categoriaId} onChange={(e) => setForm({ ...form, categoriaId: e.target.value })}>
              <option value="">Selecione a categoria</option>
              {cats?.items.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
            <Input placeholder="Descrição" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            <Input placeholder="Valor (ex: 1500.00)" type="number" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} />
            <Input type="date" value={form.dataDespesa} onChange={(e) => setForm({ ...form, dataDespesa: e.target.value })} />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setNovaDespesa(false)}>Cancelar</Button>
              <Button disabled={!form.categoriaId || !form.descricao || !form.valor || criar.isPending} onClick={() => criar.mutate()}>Lançar</Button>
            </div>
          </div>
        </Modal>
      )}

      {excluindo && (
        <Modal open onClose={() => setExcluindo(null)} title="Excluir despesa">
          <div className="space-y-3">
            {erro && <div className="rounded bg-red-50 border border-red-200 p-2 text-sm text-red-800">{erro}</div>}
            <p className="text-sm">Excluir <strong>{excluindo.descricao}</strong> de {fmtBRL(excluindo.valor)}?</p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setExcluindo(null)}>Cancelar</Button>
              <Button variant="danger" disabled={excluir.isPending} onClick={() => excluir.mutate(excluindo.id)}>Excluir</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
